'use client';

import React from "react";
import { Button, Menu, Select } from "@mantine/core";
import { usePathname, useRouter } from 'next/navigation'
import themeVariables from "@/config/theme/themeVariable";
import Link from "next/link";
import { images } from "@/utils/images";
import { useAtomValue } from "jotai";
import * as atoms from '@/config/store/globalStore'
import { handleSignOut } from "@/utils/auth";
import { useDisclosure } from "@mantine/hooks";
import { CreditImage } from "@/components/CreditIcon";
import { MENU_ITEMS } from "@/utils/menus";
import { useLanguage } from "@/hooks/useLanguage";
import { SupportedLanguage } from "@/config/store/globalStore";
import { useTranslation } from "@/hooks/useTranslation";
import { ChevronDown, LogOut, ReceiptText, Settings, SquareUserRound } from "lucide-react";

interface NavigationBarProps {
  initialLanguage?: SupportedLanguage;
}

export default function NavigationBar({ initialLanguage }: NavigationBarProps) {
  const { push } = useRouter();
  const pathname = usePathname();
  const userInfo = useAtomValue(atoms.userInfo);
  const [opened, { close, open }] = useDisclosure(false);
  const { language, changeLanguage } = useLanguage();


  
  const MobileNavbar = () => {
    const signIn = useAtomValue(atoms.signIn)
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

    // 모바일 메뉴 토글 시 스크롤 제어 (CLS 방지를 위해 scrollbar-gutter 유지)
    React.useEffect(() => {
      if (isMobileMenuOpen) {
        // 스크롤바 너비 계산
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        // 스크롤바가 사라져도 레이아웃 시프트 방지
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      } else {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }

      return () => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      };
    }, [isMobileMenuOpen]);

    const MobileMenuOverlay = () => (
      <div className={`fixed inset-0 z-[200] transition-all duration-300 ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        {/* Blurred background */}
        <div 
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu content */}
        <div className={`absolute inset-0 bg-white/95 backdrop-blur-md transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 border-b h-12">
              <div className="flex gap-1.5 items-center">
                <img className="w-7 h-7" src={images.logo} alt="KInside Logo"/>
                <div className="font-bold text-sm text-gray-900">KInside</div>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mx-8 px-2 py-4 border-b border-gray-100">
              <LanguageSwitcherMobile />
            </div>

            {/* Menu items */}
            <div className="flex-1 py-4 px-8">
              <div className="space-y-4">
                {MENU_ITEMS.map((item) => (
                  item.submenu ? (
                    <div key={item.id} className="space-y-2">
                      <div className={`text-lg text-gray-900 py-2 ${
                        item.submenu.some(subItem => pathname.includes(`/${subItem.id}`)) ? 'font-semibold' : ''
                      }`}>
                        {t(item.translationKey)}
                      </div>
                      <div className="pl-4 space-y-2">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.id}
                            href={`/${language}/${subItem.id}`}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block py-2 text-gray-600 hover:text-gray-900 ${
                              pathname.includes(`/${subItem.id}`) ? 'font-semibold text-gray-900' : ''
                            }`}
                          >
                            {t(subItem.translationKey)}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      key={item.id}
                      href={`/${language}/${item.id}`}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        if (pathname != `/${language}`) {
                          push(`/${language}#${item.id}`)
                        }
                      }}
                      className={`block py-3 text-lg hover:text-gray-700 ${
                        pathname.includes(`/${item.id}`) || (item.id === '' && pathname === `/${language}`) 
                          ? 'font-semibold text-gray-900' 
                          : 'text-gray-900'
                      }`}
                    >
                      {t(item.translationKey)}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <>
        <nav className="z-[100] w-full flex md:hidden sticky h-12 items-center top-0 bg-white border-b border-gray-200">
          <Link href={`/${language}`} className="flex gap-1.5 items-center px-4" scroll={false}>
            <img className="w-7 h-7" src={images.logo} alt="KInside Logo"/>
            <div className="font-bold text-sm text-gray-900">KInside</div>
          </Link>
          
          <div className="ml-auto flex items-center gap-2 mr-4">
            {/* Hamburger button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Mobile Hamburger Menu"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </nav>
        
        <MobileMenuOverlay />
      </>
    )
  }

  const DesktopNavBar = () => {
    const { t } = useTranslation();
    
    return (
      <nav className="z-[100] w-full hidden md:flex h-auto items-center justify-center data-[menu-open=true]:border-none sticky top-0 inset-x-0 border-b border-divider backdrop-blur-lg data-[menu-open=true]:backdrop-blur-lg backdrop-saturate-150 bg-white/75">
        <header className="z-[100] flex gap-4 w-full flex-row relative flex-nowrap items-center h-16 max-w-[1280px]">
          <div className="h-full w-full px-6 flex flex-row flex-nowrap items-center gap-6">
            <div
              className="flex gap-2 align-middle items-center cursor-pointer w-[160px]"
              onClick={() => push(`/${language}`)}
            >
              <img className="w-9 h-9" src={images.logo} alt="KInside Logo"/>
              <div className="font-bold text-base">KInside</div>
            </div>
            <div className={`flex gap-4`}>
              {MENU_ITEMS.map((item) => (
                item.submenu ? (
                  <Menu
                    key={item.id}
                    offset={14}
                    width={180}
                    radius={'md'}
                    position={'bottom-start'}
                    trigger={'click-hover'}
                  >
                    <Menu.Target>
                      <div className="p-2.5 text-[15px] text-gray-600 cursor-default hover:font-semibold hover:text-gray-700 flex gap-1 items-center">
                        {t(item.translationKey)}
                        <ChevronDown className="w-4 h-4 stroke-[1.5px]"/>
                      </div>
                    </Menu.Target>
                    <Menu.Dropdown classNames={{ dropdown: '!px-1.5 !py-1.5'}}>
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.id}
                          href={`/${language}/${subItem.id}`}
                        >
                          <Menu.Item className="px-3 py-2.5 !rounded-md my-1 !text-gray-700 cursor-pointer hover:bg-gray-50 hover:font-semibold">
                            {t(subItem.translationKey)}
                          </Menu.Item>
                        </Link>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                ) : (
                  <Link
                    key={item.id}
                    href={`/${language}/${item.id}`}
                    onClick={() => {
                      if (pathname != `/${language}`) {
                        push(`/${language}#${item.id}`)
                      }
                    }}
                  >
                    <div className="p-2.5 rounded-lg text-[15px] text-gray-600 cursor-pointer hover:text-gray-950">
                      {t(item.translationKey)}
                    </div>
                  </Link>
                )
              ))}
            </div>
            <div className="ml-auto">
              {/* <LoginButton/> */}
              <LanguageSwitcher/>
            </div>
          </div>
        </header>
      </nav>
    )
  }


  return (
    <>
      <MobileNavbar/>
      <DesktopNavBar/>
    </>
  );
}

const LoginButton = () => {
  const userInfo = useAtomValue(atoms.userInfo);
  const signIn = useAtomValue(atoms.signIn);
  const { push } = useRouter();
  const pathname = usePathname();
  const { language } = useLanguage();
  const { t } = useTranslation();

  if (signIn == null) {
    return <div className="w-[300px]"/>
  }
  
  if (!signIn) {
    return (
      <Link href={`/${language}/login`} className="w-[300px]">
        <Button classNames={themeVariables.button.dark}>
          {t('login')}
        </Button>
      </Link>
    )
  }

  return (
    <div className="flex gap-1.5 items-center w-[300px] justify-end">
      <Menu classNames={{ item: '!py-2'}} shadow={'sm'} trigger={'click-hover'} width={180} position={'bottom-end'} zIndex={101}>
        <Menu.Target>
          <div className="flex justify-end min-w-fit md:!ml-4 max-w-[160px] line-clamp-1 items-center font-normal text-[15px] text-gray-700 cursor-pointer">
            <CreditImage text={userInfo.credit}/>
            <div className="flex gap-2 items-center ml-3 leading-loose">
              {userInfo.name}님
              <ChevronDown className="w-3.5 h-3.5 stroke-[3px] text-gray-600"/>
            </div>
          </div>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<Settings className="w-4 h-4 text-gray-700"/>}
            onClick={() => push(`/${language}/my-account/profile`)}
          >
            {t('account_settings')}
          </Menu.Item>
          <Menu.Item 
            leftSection={<SquareUserRound className="w-4 h-4 text-gray-700"/>}
            onClick={() => push(`/${language}/my-account/membership`)}
          >
            {t('membership_info')}
          </Menu.Item>
          <Menu.Item
            leftSection={<ReceiptText className="w-4 h-4 text-gray-700"/>}
            onClick={() => push(`/${language}/my-account/invitation`)}
          >
            {t('referral_code')}
          </Menu.Item>
          <Menu.Item
            leftSection={<LogOut className="w-4 h-4 text-gray-700"/>}
            onClick={async () => {
              await handleSignOut();
            }}
          >
            {t('logout')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  )
}


const LanguageSwitcher = ({ className }: { className?: string }) => {
  const { language, changeLanguage } = useLanguage();

  return (
    <Select
      classNames={{
        wrapper: 'w-32',
        input: `rounded-lg !bg-gray-100 !border !border-transparent focus:!border focus:border-gray-600 focus-within:!border-gray-600 !h-9 ${className}`
      }}
      value={language}
      onChange={(value) => value && changeLanguage(value as SupportedLanguage)}
      data={[
        { label: '🇺🇸  English', value: 'en' },
        { label: '🇰🇷  한국어', value: 'ko' },
        { label: '🇯🇵  日本語', value: 'ja' },
        { label: '🇨🇳  中文', value: 'zh' }
      ]}
    />
  )
}



const LanguageSwitcherMobile = ({ className }: { className?: string }) => {
  const { language, changeLanguage } = useLanguage();

  return (
    <div className="flex items-center gap-2">
      {[
        { label: '🇺🇸  English', value: 'en' },
        { label: '🇰🇷  한국어', value: 'ko' },
        { label: '🇯🇵  日本語', value: 'ja' },
        { label: '🇨🇳  中文', value: 'zh' }
      ].map((item) => (
        <div
          key={item.value}
          className={`text-sm cursor-pointer py-1.5 px-2 rounded-lg ${language === item.value ? 'bg-gray-100' : ''}`}
          onClick={() => changeLanguage(item.value as SupportedLanguage)}
        >
          {item.label}
        </div>
      ))}
    </div>
  )
}